import { useEffect, useState } from "react";
import ClientService from "../../../services/clients/ClientService";
import useDisplaySnackbar from "../../../utils/useDisplaySnackbar";
import { getColumns, initialSort } from "../ClientRatingOverviewTableConfig";
import BasicDatatable from "../../Common/BasicDatatable";
import SlideOver from "../../Common/SlideOver";
import ClientInteractionActionView from "../ClientInteractionActionView";
import ProgressSpinner from "../../Common/ProgressSpinner";
import { convertToUTC, getNormalizedTimeZone } from "../../../utils/utils";
import useGlobalContext from "../../../context/useGlobalContext";
import { useLocation } from "react-router-dom";

const generateRows = (
  clientData: any[],
  onClientClick: (clientDomain: string, clientName: string, slideOver: boolean, clientOrgId: string, subOrgId: string,
    clickedUserMail?: string, clickedUserProfileOrgId?: string
  ) => void,
  isSlideOverOpen?: boolean,
  parentPageIdentifier?: string,
  slideOverParent?: string,
  customDateRange?: { startDate: string; endDate?: string },
  isClientInteraction?: boolean,
  activeStat?: string | null,
) => {
  return clientData?.map((item, index) => ({
    id: index,
    managerTime: `${item.firstName || ""} ${item.lastName || ""}`.trim(),
    analystTime: `${item.firstName || ""} ${item.lastName || ""}`.trim(),
    clientDomain: item.clientDomain,
    firstName: item.firstName,
    domain: item.clientDomain,
    lastName: item.lastName,
    num_meetings: item.noOfInteractions,
    managerTalkTime: item.managerTalkTime,
    analystTalkTime: item.analystTalkTime,
    userEmail: item.empEmail,
    userProfileOrgId: item.profileOrgRelationshipId,
    mrr: item.totalMrr,
    last_sentiment: item.lastThreeRatedInteractions || [],
    onClientClick: onClientClick, // pass clientName for click
    isSlideOverOpen: isSlideOverOpen,
    parentPageIdentifier: parentPageIdentifier,
    slideOverParent: slideOverParent,
    customDateRange: customDateRange,
    isClientInteraction: isClientInteraction,
    activeStat: activeStat,
    clientOrgId: item.clientOrgId,
    subOrgId: item.subOrgId,
    clientName: item.clientName
  }));
};

interface ClientStatsManagerAnalystProps {
    isSlideOverOpen: boolean;
    slideOverParent?: string;
    activeStat: string | null;
    selectedUser?: any;
    parentPageIdentifier?: string;
    customDateRange?: { startDate: string; endDate: string };
    userEmail?: string;
}
const ClientStatsManagerAnalystOverview = ({isSlideOverOpen, slideOverParent,
   activeStat, selectedUser, parentPageIdentifier, customDateRange}: ClientStatsManagerAnalystProps) => {
  const [sort, setSort] = useState(initialSort);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [columns] = useState(getColumns());
  const { showSnackbar } = useDisplaySnackbar();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [rows, setRows] = useState<any[]>([]);
  const [clientDomain, setClientDomain] = useState<string>("");
  const location = useLocation();
  const [clientName, setClientName] = useState<string>(location.state?.clientName);
  const [slideOverOpen, setSlideOverOpen] = useState(false);
  const removeCustomDateRange = true;
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const isClientInteraction = true;
  const [userDetails, setUserDetails] = useState({
    userEmail: "",
    userProfileOrgId: "",
  })
  const [clientOrgId, setClientorgid] = useState<string>(location.state?.clientOrgId);
  const [subOrgId, setSubOrgId] = useState<string>(location.state?.suborgId);
  const { accountDetails, selectedView, selectedSpace, } = useGlobalContext();
  const loggedInProfileOrgId = accountDetails?.profileOrgId ?? "";
  const isSpaceMode = selectedView === "spaces";
  const {
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
    data: clientsTalkTimeData,
  } = ClientService.useGetClientTalkTimeStats(
    {
      startTime: convertToUTC(customDateRange?.startDate || ""),
      endTime: convertToUTC(customDateRange?.endDate || ""),
      sortBy: sort?.sort_field,
      sortOrder: sort?.sort_order,
      managerStats: activeStat === "managerTime"? true : false,
      offset: pageNumber,
      limit: pageSize,
      employeePorId: selectedUser?.employeePorId || loggedInProfileOrgId,
      timeZone: getNormalizedTimeZone(),
      isSpaceMode: isSpaceMode,
      spaceIds: selectedSpace,
    },
    isSlideOverOpen && !!customDateRange?.startDate && !!customDateRange?.endDate,
  );

  useEffect(() => {
    setShowSpinner(isLoading || isFetching)
    if (isSuccess) {
      const clientList = clientsTalkTimeData?.data;
      setTotalRecord(clientsTalkTimeData?.totalRecords || 0);
      setRows(generateRows(clientList, onClientClick, isSlideOverOpen, parentPageIdentifier, slideOverParent, customDateRange, isClientInteraction,activeStat) || []);
    }
    if (isError) {
      showSnackbar("error", error?.message);
    }
  }, [isSuccess, isError, isLoading, isFetching, clientsTalkTimeData, setRows, activeStat]);

  const onClientClick = (clientDomain: string, clientName: string, slideOver: boolean, clientOrgId: string, subOrgId: string,
     clickedUserMail?: string, clickedUserProfileOrgId?: string, ) => {
    setClientDomain(clientDomain);
    setClientName(clientName);
    setSlideOverOpen(slideOver);
    setClientorgid(clientOrgId);
    setSubOrgId(subOrgId);
    setUserDetails({
      userEmail: clickedUserMail? clickedUserMail : "",
      userProfileOrgId: clickedUserProfileOrgId? clickedUserProfileOrgId : "",
    })
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const onPageChange = (value: number) => {
    setPageNumber(value);
  };

  const onPaginationChange = (pageNumber: number) => {
    onPageChange(pageNumber + 1);
  };

  const onSortingChange = (sorting: any[]) => {
    if (sorting && sorting.length > 0)
      setSort({
        sort_field: (sorting[0].id === 'managerTime' || sorting[0].id === 'analystTime') ? 'name' :
        (sorting[0].id === 'managerTalkTime' || sorting[0].id === 'analystTalkTime') ? 'talktime' :
         sorting[0].id,
        sort_order: sorting[0].desc === true ? "desc" : "asc",
      });
  };
  return (
    <>
      <div className="h-auto w-auto">
        <BasicDatatable
          columns={columns}
          rows={rows}
          rowCount={totalRecord}
          initialSort={initialSort}
          onPageSizeChange={(newPageSize) => {
            if (newPageSize) handlePageSizeChange(newPageSize);
          }}
          onSortingChange={onSortingChange}
          onPaginationChange={onPaginationChange}
          minHeight="400px"
          isBordervisible={true}
          columnVisibility={{
            lastInteraction: false,
            managerTime: activeStat === "managerTime",
            analystTime: activeStat === "analystTime",
            managerTalkTime: activeStat === "managerTime",
            analystTalkTime: activeStat === "analystTime",
          }}
        />
      </div>
      <SlideOver isOpen={slideOverOpen} onClose={() => setSlideOverOpen(false)}>
        <ClientInteractionActionView
          clientDomain={clientDomain}
          isSlideOverOpen={isSlideOverOpen}
          removeCustomDateRange={removeCustomDateRange}
          slideOverParent={slideOverParent}
          activeStat={activeStat}
          userDetails = {userDetails}
          selectedUser={selectedUser}
          parentPageIdentifier={parentPageIdentifier}
          relationshipType="client"
          customDateRange={customDateRange}
          clientOrgId={clientOrgId}
          subOrgId={subOrgId}
          clientName={clientName}
        />
      </SlideOver>
      <ProgressSpinner showSpinner={showSpinner}/>
    </>
  );
};

export default ClientStatsManagerAnalystOverview;
